// src/pages/ApplicationHome/index.tsx
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Text, Flex, Button, useToast } from '@chakra-ui/react';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { breadcrumb } from './breadcrumb';
import {
  useGetAppMenuButtons,
  useGetHomeAreaFeatures,
  usePutAppMenuButtons,
  usePutHomeAreaFeatures,
} from '@/services/queries/admin-config';
import { AppMenuButton, HomeFeaturesComponents } from '@/model';
import { HomeAreaFeatures } from './HomeAreaFeatures';
import { ApplicationComponentId } from '@/model/application-component';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { AppMenuButtons } from './AppMenuButtons';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import ControlTeamFilterSelect from '@/components/ControlTeamFilterSelect';
import { ModalFormData, modalFormSchema } from './schemas';
import { zodResolver } from '@hookform/resolvers/zod';

const hasFixedTabBar = process.env.REACT_APP_FIXED_TAB_BAR === 'true';

export const ApplicationHome = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const [selectedTeam, setSelectedTeam] = useState<string | undefined>();

  const { watch, control } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: selectedTeam || '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const watchedTeam = watch('team_uuid');

  const { data: homeAreaFeaturesData, isLoading: isLoadingHomeAreaFeatures } = useGetHomeAreaFeatures(watchedTeam || '');
  const { data: appMenuButtonsData, isLoading: isLoadingAppMenuButtons } = useGetAppMenuButtons(watchedTeam || '');

  const putHomeAreaFeatures = usePutHomeAreaFeatures();
  const putAppMenuButtons = usePutAppMenuButtons();

  const [selectedHome, setSelectedHome] = useState<HomeFeaturesComponents[]>([]);
  const [selectedShortcuts, setSelectedShortcuts] = useState<(AppMenuButton | ApplicationComponentId)[]>([]);

  useEffect(() => {
    if (watchedTeam !== selectedTeam) {
      setSelectedTeam(watchedTeam);
    }
  }, [watchedTeam, selectedTeam]);

  useEffect(() => {
    setSelectedHome(homeAreaFeaturesData?.active || []);
  }, [homeAreaFeaturesData]);

  const handleUpdateApplicationHome = useCallback(() => {
    putHomeAreaFeatures.mutate(
      {
        ...(watchedTeam ? { team_uuid: watchedTeam } : {}),
        features: selectedHome,
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_home.application_home_success'),
            status: 'success',
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_home.application_home_error'),
          });
        },
      },
    );
  }, [putHomeAreaFeatures, selectedHome, watchedTeam, toast, t]);

  const handleUpdateMenuButtons = useCallback(() => {
    putAppMenuButtons.mutate(
      {
        ...(watchedTeam ? { team_uuid: watchedTeam } : {}),
        buttons: selectedShortcuts,
      },
      {
        onSuccess: () => {
          toast({
            title: t('pages.application_home.application_home_success'),
            status: 'success',
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_home.application_home_error'),
          });
        },
      },
    );
  }, [putAppMenuButtons, selectedShortcuts, watchedTeam, toast, t]);

  return (
    <PageContent title={t('pages.application_home.application_home_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex direction='column' gap='deka'>
          <Flex direction='column' gap={2}>
            <Text fontWeight='bold' color='brand.500'>
              {t('pages.application_home.application_home_page_title')}
            </Text>
            <Text color='text.main'>{t('pages.application_home.application_home_page_subtitle')}</Text>
          </Flex>
        </Flex>

        <ControlTeamFilterSelect<ModalFormData, 'team_uuid'>
          control={control}
          name='team_uuid'
          label={t('pages.challenges_details.tabs.tab_1.input_team_label')}
          placeholder={t('pages.challenges_details.tabs.tab_1.input_team_label')}
          showFlag
          width='30%'
          usePortal={false}
          minChars={2} // opcional
          pageSize={20} // opcional
          prefetchOnOpen
        />

        <Flex direction='column'>
          <Text fontWeight='bold'>{t('pages.application_home.application_home_areas_title')}</Text>
          <Text>{t('pages.application_home.application_home_areas_description')}</Text>
        </Flex>

        <HomeAreaFeatures
          teamUUID={watchedTeam}
          isLoading={isLoadingHomeAreaFeatures}
          available={homeAreaFeaturesData?.available || []}
          selected={selectedHome}
          onChange={setSelectedHome}
        />

        <Flex justifyContent='flex-start' gap={6}>
          <Button variant='link' color='brand.500' textDecoration='underline' onClick={() => navigate(-1)}>
            {t('pages.application_home.application_home_back_button')}
          </Button>
          <Button onClick={handleUpdateApplicationHome} disabled={putHomeAreaFeatures.isLoading}>
            {t('pages.application_home.application_home_save_home_button')}
          </Button>
        </Flex>

        {!hasFixedTabBar && (
          <>
            <Flex direction='column'>
              <Text fontWeight='bold'>{t('pages.application_home.application_home_shortcuts_title')}</Text>
              <Text>{t('pages.application_home.application_home_shortcuts_description')}</Text>
            </Flex>

            <AppMenuButtons
              isLoading={isLoadingAppMenuButtons}
              available={appMenuButtonsData?.available || []}
              selected={appMenuButtonsData?.active || []}
              onChange={setSelectedShortcuts}
            />

            <Flex justifyContent='flex-start' gap={6}>
              <Button variant='link' color='brand.500' textDecoration='underline' onClick={() => navigate(-1)}>
                {t('pages.application_home.application_home_back_button')}
              </Button>
              <Button disabled={putAppMenuButtons.isLoading || selectedShortcuts.length < 3} onClick={handleUpdateMenuButtons}>
                {t('pages.application_home.application_home_save_shortcuts_button')}
              </Button>
            </Flex>
          </>
        )}
      </SectionBox>

      <CenteredSpinner isLoading={putHomeAreaFeatures.isLoading || putAppMenuButtons.isLoading} />
    </PageContent>
  );
};
